import { useState } from "react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, RefreshCcw } from "lucide-react";

/**
 * Weekly follow-recap admin controls — preview health, manual sweep,
 * or send to just yourself for a preview.
 */
export default function RecapTab({ adminUserId }) {
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const loadHealth = async () => {
    try {
      const { data } = await api.get("/admin/follow-recap/health");
      setHealth(data);
    } catch (e) {
      toast.error("Could not load recap health.");
    }
  };

  const runSweep = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/follow-recap/run", {});
      setLastRun(data);
      toast.success(`Dispatched ${data.dispatched} · delivered ${data.delivered ?? data.dispatched} · skipped ${data.skipped}`);
      loadHealth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed.");
    } finally {
      setBusy(false);
    }
  };

  const previewToMe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/follow-recap/run", { user_id: adminUserId });
      setLastRun(data);
      const dispatched = data?.dispatched || 0;
      toast.success(dispatched > 0
        ? "Preview sent to your inbox."
        : "No new followers this week — nothing to send.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-recap-tab">
      <div className="card-surface p-5">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-rose-500 mb-1">Weekly digest</div>
            <h3 className="font-display text-2xl">Follow Recap Email</h3>
            <p className="text-sm text-zinc-400 mt-1 leading-relaxed max-w-lg">
              Every Sunday at 18:00 UTC, users with new followers in the last 7 days receive a rose-tinted digest highlighting who followed them and the desires they share. Idempotent per user per ISO week.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <Button
              onClick={loadHealth}
              className="rounded-none bg-transparent border border-white/15 text-white hover:border-rose-600 text-xs"
              data-testid="recap-check-health"
            >
              <RefreshCcw size={13} /> Check status
            </Button>
            <Button
              onClick={previewToMe}
              disabled={busy || !adminUserId}
              className="rounded-none bg-transparent border border-amber-500/60 text-amber-300 hover:bg-amber-500/10 text-xs"
              data-testid="recap-preview-to-me"
            >
              <Mail size={13} /> Preview to me
            </Button>
            <Button
              onClick={runSweep}
              disabled={busy}
              className="btn-crimson rounded-none text-xs"
              data-testid="recap-run-sweep"
            >
              <Send size={13} /> {busy ? "Sending…" : "Run sweep now"}
            </Button>
          </div>
        </div>

        {health && (
          <div className="grid grid-cols-3 gap-3 mt-4" data-testid="recap-health-panel">
            <div className="bg-black/30 border border-white/5 px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">ISO Week</div>
              <div className="font-display text-lg">{health.week}</div>
            </div>
            <div className="bg-black/30 border border-white/5 px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Eligible</div>
              <div className="font-display text-lg text-emerald-400" data-testid="recap-eligible">
                {health.eligible_users}
              </div>
              <div className="text-[11px] text-zinc-500">users with new followers</div>
            </div>
            <div className="bg-black/30 border border-white/5 px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Already sent</div>
              <div className="font-display text-lg text-amber-400" data-testid="recap-already-sent">
                {health.already_sent_this_week}
              </div>
              <div className="text-[11px] text-zinc-500">this week</div>
            </div>
          </div>
        )}

        {lastRun && (
          <div className="mt-4 border-l-2 border-emerald-500 pl-3 py-2 text-xs text-emerald-400">
            Last run: dispatched {lastRun.dispatched} · delivered {lastRun.delivered ?? lastRun.dispatched} · skipped {lastRun.skipped}
          </div>
        )}
      </div>

      <div className="card-surface p-5 text-xs text-zinc-500 leading-relaxed">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">How it works</div>
        <ul className="list-disc list-inside space-y-1">
          <li>The scheduler fires the sweep at Sun 18:00 UTC. This tab is for previews + manual re-runs.</li>
          <li>Demo `@demo.pandax.com` accounts are skipped so you don't waste Resend credits.</li>
          <li>Users can turn recaps off from their profile — the API is <code className="text-zinc-300">POST /users/me/recap-optout</code>.</li>
          <li>&ldquo;Preview to me&rdquo; clears your week-stamp so you can re-send the email until it's perfect.</li>
        </ul>
      </div>
    </div>
  );
}
